import {useNavigate, useParams} from "react-router-dom";
import {ApiService} from "../api/ApiService";
import {useEffect, useMemo, useState} from "react";
import {
    Button,
    CircularProgress,
    Container,
    Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import {useNotification} from "../stores/notificationStore";
import {Client} from "../api/types/Client";
import DeleteIcon from '@mui/icons-material/Delete';
import {useApplicantCheck} from "../hooks/useApplicantCheck";
import CreateClientDialog, {CreateClientModel} from "../dialogs/CreateClientDialog";
import AgreeDialog from "../dialogs/AgreeDialog";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BreadcrumbsList from "../components/BreadcrumbsList";
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const AgentPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const agentId = useMemo(() => id ? parseInt(id) : undefined, [id]);
    const notification = useNotification();
    const {applicant} = useApplicantCheck(agentId);

    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState("");

    const handleCreateClient = async (data: CreateClientModel) => {
        if (!agentId) return;
        const createClientResponse = await ApiService.CreateClient(agentId, {
            name: data.name,
            partyTypeId: data.partyTypeId,
            matterTypeId: data.matterTypeId,
        });
        console.log(createClientResponse)
        if (createClientResponse.success) {
            notification.open("Client created successfully", "success")
            await loadClients();
        } else {
            createClientResponse.errors.forEach(a => notification.open(a, "error"));
        }
    }

    const deleteClient = async (clientId: number) => {
        if (!agentId) return;
        const response = await ApiService.DeleteClient(agentId, clientId);
        if (response.success) {
            setClients(prev => prev.filter(c => c.id !== clientId));
        } else {
            response.errors.forEach(a => notification.open(a, "error"));
        }
    }

    const loadClients = async () => {
        if (!agentId) return;
        setLoading(true);
        const getAgentClientsResponse = await ApiService.GetAgentClients(agentId);
        if (getAgentClientsResponse.success) {
            setClients(getAgentClientsResponse.data);
        } else {
            getAgentClientsResponse.errors.forEach(a => notification.open(a, "error"));
        }
        setLoading(false);
    }

    useEffect(() => {
        loadClients();
    }, [agentId])

    if (!agentId) return null;

    const renderContent = () => {
        if (loading) {
            return <CircularProgress style={{margin: 50, position: 'relative', left: "calc(50% - 70px)"}}/>
        }
        return clients.filter((client) => client.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((client) => (
                        <TableRow
                                key={client.id}
                                hover
                                style={{cursor: "pointer"}}
                                onClick={() => navigate(`/agent/${agentId}/clients/${client.id}/parties`)}
                        >
                            <TableCell component="th" scope="row">
                                <Stack direction="row" alignItems="center" justifyContent={"space-between"}>
                                    <Typography variant="body1" style={{marginRight: 10}}
                                                onClick={() => navigate(`/agent/${agentId}/clients/${client.id}/parties`)}>
                                        {client.name}
                                    </Typography>
                                    <AgentMenu client={client} onDeleteClick={a => deleteClient(a.id)}/>
                                </Stack>
                            </TableCell>
                        </TableRow>
                ))
    }

    return (
            <>
                <Container sx={{paddingTop: 2}}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <BreadcrumbsList links={[{text: applicant?.name ?? "", href: `/parties/${applicant?.id}`}]}
                                             lastItem={{text: "Clients"}}/>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid spacing={2} container>
                                <Grid item xs={12} sm={7} lg={9}>
                                    <TextField variant={"outlined"}
                                               size={"small"}
                                               style={{width: "100%"}}
                                               placeholder={"Search"}
                                               value={searchTerm}
                                               onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5} lg={3}>
                                    <CreateClientDialog agentId={agentId} onCreate={handleCreateClient}>
                                        {(open, _) =>
                                                <Button onClick={open}
                                                        variant={"contained"}
                                                        style={{width: "100%"}}
                                                        startIcon={<PersonAddIcon/>}
                                                >
                                                    Create client
                                                </Button>}
                                    </CreateClientDialog>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <TableContainer component={Paper}>
                                <Table aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody style={{minHeight: 100}}>
                                        {renderContent()}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>

                </Container>
            </>
    )
}

export default AgentPage;


interface AgentMenuProps {
    onDeleteClick: (client: Client) => void;
    client: Client;
}

const AgentMenu = ({client, onDeleteClick}: AgentMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {

        setAnchorEl(null);
    };

    const handleDeleteClick = async () => {
        if (onDeleteClick) {
            await onDeleteClick(client);
        }
        handleClose();
    }

    return (
            <>
                <IconButton onClick={handleClick}>
                    <MoreVertIcon/>
                </IconButton>
                <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        slotProps={{
                            root: {
                                onClick: (e) => e.stopPropagation(),
                            },
                        }}
                >
                    <AgreeDialog onDelete={handleDeleteClick}
                                 text={`Do you really want to delete this client (${client.name})?`}>
                        {
                            (open, _) => (
                                    <MenuItem onClick={open} color={"red"} sx={{color: 'error.main'}}>
                                        <ListItemIcon sx={{color: 'inherit'}}>
                                            <DeleteIcon fontSize="small"/>
                                        </ListItemIcon>
                                        <ListItemText>Delete</ListItemText>
                                    </MenuItem>
                            )
                        }
                    </AgreeDialog>
                </Menu>
            </>
    )
}